// The control for a Currency custom field on every surface that edits one: the design-system NumberField with
// the field's currency symbol on the side Forecast's currency list places it (before the amount for $, after it
// for €) and exactly 2 decimals, as every Forecast amount has. It saves when the user leaves the box or presses
// Enter, the only times the NumberField reports an amount, rounded to 2 decimals. An unchanged amount saves
// nothing and an emptied box clears the value. An amount the service would refuse (more than 9 digits before
// the decimal point) is not saved and says why under the box. Read-only, it is the formatted amount as text, in
// the separators of the viewer's locale as Forecast's other money, or nothing when it is not set. A click on the box never reaches the row behind it: a phase header row toggles
// the phase on click.
import React, {useEffect, useState} from 'react';
import Styled from 'styled-components';
import {useIntl} from 'react-intl';
import {NumberField, Text} from '@forecast-it/design-system';
import {
	AMOUNT_DECIMALS,
	amountToStored,
	currencyEntry,
	formatCustomFieldAmount,
	isValidAmount,
	sameAmount,
	storedAmount,
} from '../forecast-app/shared/util/customFieldValueValidation';

// The box fills its cell and may shrink to it; a long amount scrolls inside the box.
const Frame = Styled.div`
	width: 100%;
	min-width: 0;
`;

type CustomFieldCurrencyInputProps = {
	value: string | null | undefined;
	// The field's ISO 4217 code, which picks the symbol and the side of the amount it sits on.
	currencyCode: string | null | undefined;
	// Called with the amount to store ('1250.00'), or null to clear it.
	onChange: (value: string | null) => void;
	// The field's name: the box's accessible name, never the field's key.
	label: string;
	readOnly?: boolean;
	// Off while another save on the same record is in flight.
	disabled?: boolean;
	// A message the caller shows under the box, such as a save it knows was refused.
	errorText?: string;
	'data-cy'?: string;
};

export const CustomFieldCurrencyInput = ({
	value,
	currencyCode,
	onChange,
	label,
	readOnly,
	disabled,
	errorText,
	'data-cy': dataCy = 'custom-field-currency-input',
}: CustomFieldCurrencyInputProps): React.JSX.Element | null => {
	const {formatMessage, locale} = useIntl();
	const stored = storedAmount(value);
	// What the box shows: the stored amount, or the last amount typed while it is refused.
	const [amount, setAmount] = useState(stored);
	const [refused, setRefused] = useState(false);
	useEffect(() => {
		setAmount(storedAmount(value));
		setRefused(false);
	}, [value]);

	if (readOnly) {
		const text = formatCustomFieldAmount(value, currencyCode, locale);
		return text ? (
			<Text size="2" data-cy={dataCy}>
				{text}
			</Text>
		) : null;
	}

	const {symbol, prefixed} = currencyEntry(currencyCode);
	const commit = (next: number) => {
		setAmount(next);
		const nextValue = Number.isNaN(next) ? null : amountToStored(next);
		const valid = nextValue === null || isValidAmount(nextValue);
		setRefused(!valid);
		if (valid && !sameAmount(next, stored)) onChange(nextValue);
	};

	return (
		<Frame onClick={e => e.stopPropagation()}>
			<NumberField
				aria-label={label}
				data-cy={dataCy}
				value={amount}
				onChange={commit}
				prefixText={prefixed ? symbol : undefined}
				suffixText={prefixed ? undefined : symbol}
				minimumFractionDigits={AMOUNT_DECIMALS}
				maximumFractionDigits={AMOUNT_DECIMALS}
				align={NumberField.ALIGN.RIGHT}
				size="small"
				allowEmpty
				disabled={disabled}
				errorText={refused ? formatMessage({id: 'custom_field_value.amount_invalid'}) : errorText}
			/>
		</Frame>
	);
};

export default CustomFieldCurrencyInput;
